import { Link } from "@tanstack/react-router";
import confetti from "canvas-confetti";
import { useEffect, useMemo, useRef } from "react";
import type {
  Store,
  TransactionGroup,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import CheckCircle from "~icons/material-symbols/check-circle-outline";
import { css } from "../../../styled-system/css";
import audio from "../../assets/audio/kessai-kanryou.mp3";
import Header from "../../components/header";
import { formatDate } from "../../libs/date";
import { t } from "../../libs/i18n";
import { formatNumber } from "../../libs/utils";
import PointUsageBlock from "./components/point-usage-block";
import StoreBlock from "./components/store-block";
import type { PayForm } from "./use-pay-form";

type PayCompleteProps = {
  store: Store;
  payForm: PayForm;
  transactionGroup: TransactionGroup | null;
};

export default function PayComplete({
  store,
  payForm,
  transactionGroup,
}: PayCompleteProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const confettiInstanceRef = useRef<ReturnType<typeof confetti.create> | null>(
    null,
  );
  const currentTime = useMemo(
    () => formatDate(new Date(), "dateTimeSeconds"),
    [],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!confettiInstanceRef.current) {
      // Android WebView での白画面回避のため worker を無効化したインスタンスを利用
      confettiInstanceRef.current = confetti.create(undefined, {
        useWorker: false,
        resize: true,
      });
    }

    confettiInstanceRef.current?.({
      spread: 360,
      ticks: 500,
      gravity: 0.3,
      decay: 0.92,
      startVelocity: 14,
      origin: { y: 0.3 },
      particleCount: 50,
      scalar: 1.6,
      shapes: ["square"],
    });

    if (audioRef.current) {
      audioRef.current.play();
    }

    return () => {
      confettiInstanceRef.current?.reset();
    };
  }, []);

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        bg: "white",
      })}
    >
      {/* biome-ignore lint/a11y/useMediaCaption: <explanation> */}
      <audio ref={audioRef} src={audio} />
      <Header title="" theme="dark" />
      <div
        className={css({
          px: "16px",
          pt: "16px",
          display: "flex",
          flexDirection: "column",
          transform: "rotate(180deg)",
          color: "white",
          bg: "#3D4756",
          marginTop: "-1px",
        })}
      >
        <StoreBlock store={store} reverse size="large" />

        <div
          className={css({
            fontSize: "20px",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: 0,
          })}
        >
          <div
            className={css({
              fontSize: "48px",
              textAlign: "center",
              position: "relative",
            })}
          >
            <span>{formatNumber(payForm.payAmount)}</span>

            <div
              className={css({
                position: "absolute",
                fontSize: "16px",
                bottom: "10px",
                right: "-22px",
              })}
            >
              pt
            </div>
          </div>
        </div>

        <div
          className={css({
            display: "flex",
            justifyContent: "center",
          })}
        >
          {currentTime}
        </div>
        <div
          className={css({
            textAlign: "center",
            fontSize: "14px",
          })}
        >
          {t("取引ID:")} {transactionGroup?.id}
        </div>
      </div>

      <div
        className={css({
          bg: "surface.accentSuccess",
          color: "white",
          display: "flex",
          justifyContent: "center",
          p: "6px",
          fontSize: "14px",
          gap: "6px",
        })}
      >
        <div
          className={css({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          })}
        >
          <CheckCircle />
        </div>
        <div>{t("支払い完了")}</div>
      </div>

      <div
        className={css({
          p: "16px",
          pb: "64px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          bg: "surface.accentSuccessLight",
          minHeight: "100%",
          flex: 1,
        })}
      >
        <StoreBlock store={store} size="large" />
        <div
          className={css({
            rounded: "xl",
            p: "16px",
            border: "1px solid",
            borderColor: "border.primary",
          })}
        >
          <div
            className={css({
              fontSize: "20px",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: 0,
            })}
          >
            <div
              className={css({
                fontSize: "48px",
                color: "text.accentSuccess",
                textAlign: "center",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              })}
            >
              <span
                className={css({
                  fontSize: "32px",
                  marginTop: "4px",
                })}
              >
                <CheckCircle />
              </span>
              <span>{formatNumber(payForm.payAmount)}</span>
            </div>
          </div>
        </div>

        <Link
          to="/"
          replace // 支払い完了画面を閉じるためにreplaceを指定
          className={css({
            color: "text.primary",
            fontWeight: "bold",
            textAlign: "center",
            fontSize: "16px",
            mt: "16px",
            mb: "16px",
          })}
        >
          {t("閉じる")}
        </Link>

        <PointUsageBlock balances={payForm.balances} />
      </div>
    </div>
  );
}
